"use client";

/**
 * Lightweight client-side logger for browser code.
 *
 * Wraps `console.*` with structured metadata so logs are consistent
 * across the codebase. In production builds, debug-level messages
 * are suppressed automatically.
 *
 * Usage:
 *   import { clientLogger } from "@/lib/client-logger";
 *   clientLogger.info("Component mounted", { component: "Dashboard" });
 *   clientLogger.error("Failed to fetch", { err });
 */

type LogData = Record<string, unknown>;

const isProd = process.env.NODE_ENV === "production";

function formatMsg(module: string | undefined, msg: string, data?: LogData) {
  const prefix = module ? `[${module}]` : "";
  return data
    ? [`${prefix} ${msg}`.trim(), data]
    : [`${prefix} ${msg}`.trim()];
}

function createClientLogger(module?: string) {
  return {
    debug(msg: string, data?: LogData) {
      if (!isProd) console.debug(...formatMsg(module, msg, data));
    },
    info(msg: string, data?: LogData) {
      if (!isProd) console.info(...formatMsg(module, msg, data));
    },
    warn(msg: string, data?: LogData) {
      console.warn(...formatMsg(module, msg, data));
    },
    error(msg: string, data?: LogData) {
      console.error(...formatMsg(module, msg, data));
    },
    child(childModule: string) {
      return createClientLogger(module ? `${module}:${childModule}` : childModule);
    },
  };
}

export const clientLogger = createClientLogger();
